import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnChanges, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { getUVEState } from '@dotcms/uve';
import { Observable, combineLatest, map } from 'rxjs';

import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';
import { whenTruthy } from 'ish-core/utils/operators';

import { DotCmsUveFacade } from '../../facades/dot-cms-uve.facade';
import { VideoContentlet } from '../../models/dot-cms-uve-contentlet.model';
import { BlockEditorContent, BlockEditorNode } from '../../models/dotcms-block-editor.model';

@Component({
  selector: 'custom-dot-cms-video',
  templateUrl: './dot-cms-uve-video.component.html',
  styleUrls: ['./dot-cms-uve-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, NgStyle, AsyncPipe],
})
export class DotCmsUveVideoComponent implements OnChanges {
  content = input<BlockEditorContent>();
  attrs = input<BlockEditorNode['attrs']>();
  contentlet = input<VideoContentlet>();
  isInSideEditor: boolean;

  private dotCmsUveFacade = inject(DotCmsUveFacade);
  private elementRef = inject(ElementRef);
  private gtmFacade = inject(GoogleTagManagerFacade);

  readonly hosts = toSignal(this.dotCmsUveFacade.getDotCmsHost$, { initialValue: '' });

  readonly $srcURL = computed(() => {
    const host = this.hosts();
    // @ts-ignore
    const src = this.content()?.attrs?.src;
    return src ? `${host}${src}` : '';
  });

  triggerCount = 1;
  ngOnChanges(): void {
    this.isInSideEditor = !!getUVEState();

    this.gtmFacade.setupIntersectionObserverForGTMEvent(
      this.elementRef.nativeElement,
      [this.contentlet()],
      this.triggerCount,
      'view_promotion'
    );
  }
  getVideoHeight$(contentlet: VideoContentlet): Observable<string> {
    return this.dotCmsUveFacade.getVideoHeight(contentlet);
  }

  buildVideoSrc(): Observable<string | null> {
    return combineLatest([this.dotCmsUveFacade.getDotCmsHost$.pipe(whenTruthy())]).pipe(
      whenTruthy(),
      map(([host]) => {
        const content = this.contentlet() as unknown as Record<string, any>;

        // Case 1: asset present
        if (content?.asset?.idPath) {
          return `${host}${content.asset.idPath}`;
        }

        // Case 2: fileAsset present (object or string)
        const fileAsset = content?.fileAsset;

        if (fileAsset && typeof fileAsset === 'object' && fileAsset.idPath) {
          if (this.isInSideEditor) {
            return `${host}${fileAsset.idPath}?publish=${this.contentlet()?.publish}`;
          }

          return `${host}${fileAsset.idPath}`;
        }

        if (typeof fileAsset === 'string' && fileAsset) {
          let normalized = fileAsset;

          // Normalize /data/shared/assets → /dA
          if (normalized.startsWith('/data/shared/assets')) {
            normalized = normalized.replace('/data/shared/assets', '/dA');
          }
          normalized = normalized.replace(/^\/dA\/[^/]+\/[^/]+\//, '/dA/');

          // Encode filename part
          const lastSlash = normalized.lastIndexOf('/');
          const base = normalized.substring(0, lastSlash + 1);
          const fileName = normalized.substring(lastSlash + 1);
          const encodedFileName = encodeURIComponent(fileName);

          return `${host}${base}${encodedFileName}`;
        }

        return null;
      })
    );
  }

  getVideoClasses(contentlet: VideoContentlet) {
    return this.dotCmsUveFacade.getVideoClasses(contentlet);
  }
}
